// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IUser } from '@/types';

const registerUser = createAsyncThunk('auth/register', async (userData: { email: string; password: string }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/auth/register', userData);
    return response.data.user;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Registration failed');
  }
});

const getUser = createAsyncThunk('auth/getProfile', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get('/auth/profile');
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'User retrieval failed');
  }
});

const updateUser = createAsyncThunk('auth/updateProfile', async (updateData: Partial<IUser>, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch('/auth/profile', updateData);
    return response.data.user;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'User update failed');
  }
});

const verifyEmail = createAsyncThunk('auth/verifyEmail', async (token: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/auth/verify-email?token=${token}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Email verification failed');
  }
});

const resendVerificationEmail = createAsyncThunk('auth/resendVerificationEmail', async (email: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/auth/resend-verification', { email });
    return response.data.user;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Failed to send verification email');
  }
});

const forgetPassword = createAsyncThunk('auth/forgetPassword', async (email: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/auth/forget-password', { email });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Failed to send forget password email');
  }
});

const resetPassword = createAsyncThunk('auth/resetPassword', async (params: { token: string; newPassword: string }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch('/auth/reset-password', { token: params.token, newPassword: params.newPassword });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Failed to reset password');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
    isLoggedIn: false,
  },
  reducers: {
    logout: (state) => {
      state.data = null;
      state.error = null;
      state.isLoggedIn = false;
      localStorage.removeItem('token');
    },
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    updateUserField: (state, action) => {
      if (state.data) {
        state.data = { ...state.data, [action.payload.field]: action.payload.value };
      }
    },
  },
  extraReducers: (builder) => {
    builder

      // registerUser
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getUser
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateUser
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.isLoggedIn = true;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // verifyEmail
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // resendVerificationEmail
      .addCase(resendVerificationEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerificationEmail.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // forgetPassword
      .addCase(forgetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // resetPassword
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, setIsLoggedIn, updateUserField } = authSlice.actions;
export { forgetPassword, getUser, registerUser, resendVerificationEmail, resetPassword, updateUser, verifyEmail };
export default authSlice.reducer;
